<template>
  <!-- ===================================================================
       CATALOGUE — écran de l'application, balisage propre.
       Le site garde le sien, intact, dans le `v-else` plus bas.
       Densité privilégiée sur l'effet : c'est l'écran où l'on parcourt.
       Vignette latérale de 120px — les photos sont plafonnées à 414x219
       par le serveur, elles y sont donc nettes en densité double.
       =================================================================== -->
  <div v-if="isNative" class="ac-cat">

    <header class="ac-top">
      <div class="ac-top__line">
        <h1 class="ac-top__title">Catalogue</h1>
        <span class="ac-top__count">
          {{ acVisibles.length }} {{ acVisibles.length > 1 ? 'activités' : 'activité' }}
        </span>
      </div>

      <!-- La recherche n'apparaît qu'au-delà de 8 sorties : en dessous, elle
           ne servirait à rien et encombrerait l'écran. -->
      <div v-if="acRechercheUtile" class="ac-search">
        <svg class="ac-search__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="11" cy="11" r="7.5"/><path d="M21 21l-4.3-4.3"/></svg>
        <input
          v-model="acRecherche"
          type="search"
          class="ac-search__field"
          placeholder="Ville, club, activité…"
          aria-label="Rechercher une activité"
        >
        <button
          v-if="acRecherche"
          type="button"
          class="ac-search__clear"
          aria-label="Effacer la recherche"
          @click="acRecherche = ''"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="ac-sports" role="tablist" aria-label="Filtrer par sport">
        <button
          type="button" role="tab" class="ac-sport"
          :class="{ 'is-on': !selectedFilter }"
          :aria-selected="!selectedFilter"
          @click="updateFilter(null)"
        >Tous les sports</button>
        <button
          v-for="sport in sportNames" :key="sport"
          type="button" role="tab" class="ac-sport"
          :class="{ 'is-on': selectedFilter === sport }"
          :aria-selected="selectedFilter === sport"
          @click="updateFilter(sport)"
        >{{ sport }}</button>
      </div>

      <div class="ac-formats" role="tablist" aria-label="Filtrer par format">
        <button type="button" role="tab" class="ac-format" :class="{ 'is-on': acType === 'tous' }" :aria-selected="acType === 'tous'" @click="acType = 'tous'">Tous formats</button>
        <button type="button" role="tab" class="ac-format" :class="{ 'is-on': acType === 'sessions' }" :aria-selected="acType === 'sessions'" @click="acType = 'sessions'">Sessions</button>
        <button type="button" role="tab" class="ac-format" :class="{ 'is-on': acType === 'sejours' }" :aria-selected="acType === 'sejours'" @click="acType = 'sejours'">Stages et séjours</button>
      </div>
    </header>

    <div class="ac-body">
      <!-- Chargement -->
      <div v-if="loading" class="ac-list" aria-hidden="true">
        <span v-for="n in 4" :key="n" class="ac-row ac-row--ghost"></span>
      </div>

      <!-- Résultats -->
      <div v-else-if="acVisibles.length" class="ac-list">
        <article
          v-for="l in acVisibles"
          :key="l.cle"
          class="ac-row"
          :class="{ 'is-full': l.complet }"
        >
          <div
            class="ac-row__hit"
            role="link"
            tabindex="0"
            @click="l.nbDates > 1 ? showDatesModal(l.entree) : $router.push({ name: 'OfferDetailsPage', params: { id: l.id } })"
            @keydown.enter="$router.push({ name: 'OfferDetailsPage', params: { id: l.id } })"
          >
            <div class="ac-row__thumb">
              <img :src="l.image" :alt="''" width="414" height="219" loading="lazy">
              <span v-if="l.type" class="ac-row__type">{{ l.type }}</span>
              <span v-if="l.complet" class="ac-row__done">Complet</span>
            </div>

            <div class="ac-row__main">
              <div class="ac-row__head">
                <span class="ac-row__sport">{{ l.sport }}</span>
                <span v-if="l.nbDates > 1" class="ac-row__dates">{{ l.nbDates }} dates</span>
              </div>

              <h2 class="ac-row__title">{{ l.titre }}</h2>

              <p class="ac-row__line">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5"/><path d="M16 2.5v4M8 2.5v4M3 10h18"/></svg>
                {{ l.date ? formatFrenchDate(l.date.split(' ')[0]) : '' }}
              </p>
              <p v-if="l.ville" class="ac-row__line">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M20.5 10.5c0 6.5-8.5 12-8.5 12s-8.5-5.5-8.5-12a8.5 8.5 0 0 1 17 0z"/><circle cx="12" cy="10.3" r="2.8"/></svg>
                {{ l.ville }}
              </p>
              <p v-if="l.club" class="ac-row__club">{{ l.club }}</p>

              <div class="ac-row__foot">
                <span class="ac-row__level">{{ l.niveau || 'Tous niveaux' }}</span>
                <span v-if="l.prix" class="ac-row__price">{{ l.prix }}<span>&nbsp;€</span></span>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Aucun résultat -->
      <div v-else class="ac-void">
        <span class="ac-void__mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7.5"/><path d="M21 21l-4.3-4.3M8 11h6"/></svg>
        </span>
        <h2 class="ac-void__title">Aucun résultat</h2>
        <p class="ac-void__text">Aucune sortie ne correspond à ces filtres. Élargis la recherche pour voir tout le catalogue.</p>
        <button type="button" class="ac-void__reset" @click="updateFilter(null); acType = 'tous'; acRecherche = ''">
          Réinitialiser les filtres
        </button>
      </div>
    </div>
  </div>

  <!-- ===== Site web : balisage d'origine, strictement inchangé ===== -->
  <template v-else>

  <div v-if="loading" class="loading-overlay">
    <div class="spinner"></div>
  </div>

  <section class="hero2"></section>
  
  <section class="activities">
    <h2>NOS DIFFÉRENTES ACTIVITÉS</h2>
    <ul class="activity-list">
      <li v-for="sport in sportNames" :key="sport"
        @click.prevent="updateFilter(sport)"
        @mouseover="hoveredSport = sport"
        @mouseleave="hoveredSport = null" :class="{ 'grayed-out': isGrayedOut(sport) }">
        <img :src="sportBanner(sport)" :alt="sport">
        <span>{{ sport }}</span>
      </li>
    </ul>
  </section>

  <section class="upcoming-activities">
    <h2>TOUTES LES PROCHAINES ACTIVITÉS</h2>
    <div class="activities-grid">
      <template
        v-for="entry in sortedOfferDetails"
        :key="`${entry.offerId}-${entry.specificCriterion.date}`"
      >
        <RouterLink
          v-if="entry.CurrentQuantity !== '0'"
          :to="{ name: 'OfferDetailsPage', params: { id: entry.ID_Criteria } }"
          custom
        >
          <template v-slot="{ navigate }">
            <div class="activity" @click="entry.variants && entry.variants.length > 1 ? showDatesModal(entry) : navigate()">
              <img
                :src="apiImg(entry.images[0])"
                :alt="`Image for ${entry.offerDetails.sport} on ${formatFrenchDate(entry.specificCriterion.date.split(' ')[0])}`"
              />
              <div class="activity-orga">
                <p>ORGA OUICSPORT</p>
              </div>
              <div class="activity-type">
                <p>{{ entry.offerDetails.type }}</p>
              </div>
              <div
                class="activity-date"
                v-if="!['Stage à la carte','Séjour','Stage','Stage/Séjour'].includes(entry.offerDetails.type)"
              >
                <p>
                  {{ formatFrenchDate(entry.specificCriterion.date.split(' ')[0]) }}
                  <span
                    v-if="entry.variants && entry.variants.length > 1"
                    class="dates-badge"
                    @click.stop="showDatesModal(entry)"
                  >
                    +{{ entry.variants.length - 1 }} {{ entry.variants.length - 1 === 1 ? 'date' : 'dates' }}
                  </span>
                </p>
              </div>
              <div
                class="activity-date"
                v-else
              >
                <p>
                  {{ formatDateRange(entry.specificCriterion.date) }}
                  <span
                    v-if="entry.variants && entry.variants.length > 1"
                    class="dates-badge"
                    @click.stop="showDatesModal(entry)"
                  >
                    +{{ entry.variants.length - 1 }} {{ entry.variants.length - 1 === 1 ? 'date' : 'dates' }}
                  </span>
                </p>
              </div>
              <div class="activity-info">
                <p
                  v-if="entry.offerDetails.titleOffers"
                  class="activity-name"
                  v-html="entry.offerDetails.titleOffers"
                ></p>
                <p
                  v-else
                  class="activity-name"
                >
                  {{ entry.offerDetails.type + ' à ' + entry.offerDetails.finalCity }}
                </p>
                <p class="activity-session">{{ entry.offerDetails.sport }}</p>
                <div class="activity-meta">
                  <p class="activity-place">{{ entry.offerDetails.finalCity }}</p>
                  <p class="activity-price">
                    <span v-if="typeof entry.groupPrice === 'string' && entry.groupPrice.includes('-')">
                      De {{ entry.groupPrice.split('-')[0] }}€ à {{ entry.groupPrice.split('-')[1] }}€
                    </span>
                    <span v-else>
                      À partir de {{ entry.groupPrice }}€
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </template>
        </RouterLink>

        <div
          v-else
          class="activity activity--complete"
        >
          <img
            :src="apiImg(entry.images[0])"
            :alt="`Image for ${entry.offerDetails.sport} on ${formatFrenchDate(entry.specificCriterion.date.split(' ')[0])}`"
          />
        
          <div class="activity-type">
            <p>{{ entry.offerDetails.type }}</p>
          </div>
          <div
            class="activity-date"
            v-if="!['Stage à la carte','Séjour','Stage','Stage/Séjour'].includes(entry.offerDetails.type)"
          >
            <p>
              {{ formatFrenchDate(entry.specificCriterion.date.split(' ')[0]) }}
              <span
                v-if="entry.variants && entry.variants.length > 1"
                class="dates-badge"
                @click.stop="showDatesModal(entry)"
              >
                +{{ entry.variants.length - 1 }} {{ entry.variants.length - 1 === 1 ? 'date' : 'dates' }}
              </span>
            </p>
          </div>
          <div
            class="activity-date"
            v-else
          >
            <p>
              {{ formatDateRange(entry.specificCriterion.date) }}
              <span
                v-if="entry.variants && entry.variants.length > 1"
                class="dates-badge"
                @click.stop="showDatesModal(entry)"
              >
                +{{ entry.variants.length - 1 }} {{ entry.variants.length - 1 === 1 ? 'date' : 'dates' }}
              </span>
            </p>
          </div>
          <div class="activity-info">
            <p
              v-if="entry.offerDetails.titleOffers"
              class="activity-name"
              v-html="entry.offerDetails.titleOffers"
            ></p>
            <p
              v-else
              class="activity-name"
            >
              {{ entry.offerDetails.type + ' à ' + entry.offerDetails.finalCity }}
            </p>
            <p class="activity-session">{{ entry.offerDetails.sport }}</p>
            <div class="activity-meta">
              <p class="activity-place">{{ entry.offerDetails.finalCity }}</p>
              <p class="activity-price">
                <span v-if="typeof entry.groupPrice === 'string' && entry.groupPrice.includes('-')">
                  De {{ entry.groupPrice.split('-')[0] }}€ à {{ entry.groupPrice.split('-')[1] }}€
                </span>
                <span v-else>
                  À partir de {{ entry.groupPrice }}€
                </span>
              </p>
            </div>
          </div>
          <span class="activity-complete-label">Complet</span>
        </div>
      </template>
    </div>
  </section>

  <!-- Modal pour afficher les dates disponibles -->
  <div v-if="showModal" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <button class="modal-close" @click="closeModal">&times;</button>

      <!-- En-tête du modal avec image -->
      <div class="modal-header">
        <img :src="getFullSizeImage(selectedOffer?.images[0])" :alt="selectedOffer?.offerDetails.sport" class="modal-image">
        <div class="modal-title-overlay">
          <h3>{{ decodeHtml(selectedOffer?.offerDetails.titleOffers || (selectedOffer?.offerDetails.type + ' à ' + selectedOffer?.offerDetails.finalCity)) }}</h3>
          <p class="modal-subtitle">{{ selectedOffer?.offerDetails.sport }}</p>
        </div>
      </div>

      <!-- Liste des dates -->
      <div class="modal-dates-list">
        <div
          v-for="(variant, index) in selectedOffer?.variants"
          :key="index"
          :class="['modal-date-item', { 'modal-date-complete': variant.CurrentQuantity === '0' }]"
          @click="variant.CurrentQuantity !== '0' ? selectDate(variant) : null"
        >
          <div class="modal-date-content">
            <div class="modal-date-info">
              <div class="modal-date-text">
                <span v-if="!['Stage à la carte','Séjour','Stage','Stage/Séjour'].includes(variant.offerDetails.type)">
                  {{ formatFrenchDate(variant.specificCriterion.date.split(' ')[0]) }}
                </span>
                <span v-else>
                  {{ formatDateRange(variant.specificCriterion.date) }}
                </span>
              </div>
              <div class="modal-date-details">
                <span class="modal-location">{{ variant.offerDetails.finalCity }}</span>
                <!-- Affichage de tous les créneaux groupés (niveau + horaire ensemble) -->
                <div v-if="Array.isArray(variant.specificCriterion.level)" class="modal-criteria">
                  <span v-for="(slot, idx) in variant.specificCriterion.level" :key="idx" class="modal-criterion-item">
                    {{ slot.criteria.map(c => c.Value).join(' ') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="modal-price-section">
              <div class="modal-price">
                <span v-if="typeof variant.specificCriterion.price === 'string' && variant.specificCriterion.price.includes('-')">
                  {{ variant.specificCriterion.price.split('-')[0] }}€ - {{ variant.specificCriterion.price.split('-')[1] }}€
                </span>
                <span v-else>
                  {{ variant.specificCriterion.price }}€
                </span>
              </div>
              <span v-if="variant.CurrentQuantity === '0'" class="modal-complete-badge">Complet</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </template>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { apiImg, sportBanner, isNative } from '@/api/base';

export default {
  name: 'OffersList',
  data() {
    return {
      hoveredSport: null,
    };
  },
  methods: {
    formatFrenchDate(dateStr) {
      const [day, month, year] = dateStr.split('/');
      const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ];
      return `${day} ${months[parseInt(month) - 1]} ${year}`;
    },
    formatDateRange(dateStr) {
      const parts = dateStr.split(' ');
      const start = parts[0];
      const end   = parts[parts.length - 2];

      if (start === end) {
        return this.formatFrenchDate(start);
      }
      return `${this.formatFrenchDate(start)} au ${this.formatFrenchDate(end)}`;
    },
    decodeHtml(html) {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    },
    getFullSizeImage(imageUrl) {
      if (!imageUrl) return imageUrl;
      return apiImg(imageUrl.replace(/\/\d+x\d+\//g, '/'));
    },
  },
  setup() {
    const router = useRouter();
    const offers = ref([]);
    const selectedFilter = ref(null);
    const sportNames = ref([]);
    const offerDetails = ref([]);
    const loading = ref(true);
    const error = ref(false);
    const offerDetailsBackup = ref([]);
    const showModal = ref(false);
    const selectedOffer = ref(null);
    

    const formatOfferDates = (offers) => {
      return offers
        .map(offer => {
          if (typeof offer.dates !== 'string' || offer.dates.trim() === '') {
            console.error('Offre sans propriété `dates` valide :', offer);
            return {
              ...offer,
              futureDates: []
            };
          }

          const futureDates = offer.dates
            .split(',')
            .map(dateStr => {
              const [year, month, day] = dateStr.trim().split('-');
              return new Date(`${month}/${day}/${year}`);
            })
            .filter(date => date >= new Date())
            .sort((a, b) => a - b);

          return {
            ...offer,
            futureDates: futureDates.map(date => date.toLocaleDateString('fr-FR'))
          };
        })
        .sort((a, b) => {
          const a0 = a.futureDates[0];
          const b0 = b.futureDates[0];
          if (!a0 || !b0) return 0;
          const da = new Date(a0.split('/').reverse().join('-'));
          const db = new Date(b0.split('/').reverse().join('-'));
          return da - db;
        });
    };


    const fetchOffers = async () => {
      try {
        const response = await axios.get('https://www.ouicsport.fr/api/search/by-sport-json');
        offers.value = JSON.parse(response.data);
        sportNames.value = offers.value.offers.map(offer => offer.sportName);
      } catch (error) {
        console.error('Erreur lors de la récupération des offres:', error);
      }
    };

    const filteredOffers = computed(() => {
      if (selectedFilter.value) {
        return offerDetailsBackup.value.filter(offerDetails => offerDetails.offerDetails.sport === selectedFilter.value);
      }
      return offerDetailsBackup.value;
    });

    const updateFilter = (sport) => {
      selectedFilter.value = sport;
      offerDetails.value = filteredOffers.value;
    };

    const isGrayedOut = (sport) => {
      return selectedFilter.value && selectedFilter.value !== sport;
    };

    const segmentOffersByCriteria = (offerDetails) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const isStageSejourType = (type) =>
        ['Stage à la carte', 'Séjour', 'Stage', 'Stage/Séjour'].includes(type)

      const segmentedOffers = []

      offerDetails.forEach((offer) => {
        const criteria = offer.criteria || []
        const type = offer.offerDetails?.type

        if (isStageSejourType(type)) {
          const hasPastDate = criteria.some((c) => {
            const [day, month, year] = c.date.split(' ')[0].split('/')
            const d = new Date(+year, +month - 1, +day)
            return d.getTime() < today.getTime()
          })
          if (hasPastDate) {
            return
          }
        }

        const futureCriteria = criteria.filter((c) => {
          const [day, month, year] = c.date.split(' ')[0].split('/')
          const d = new Date(+year, +month - 1, +day)
          return d.getTime() >= today.getTime()
        })

        if (futureCriteria.length === 0) {
          return
        }

        let segmentedOffer = JSON.parse(JSON.stringify(offer))
        delete segmentedOffer.criteria
        delete segmentedOffer.futureDates

        if (isStageSejourType(type)) {
          const datesSorted = futureCriteria
            .map((c) => c.date)
            .sort((a, b) => {
              const pa = new Date(a.split(' ')[0].split('/').reverse().join('-'))
              const pb = new Date(b.split(' ')[0].split('/').reverse().join('-'))
              return pa - pb
            })
          const firstDate = datesSorted[0]
          const lastDate = datesSorted[datesSorted.length - 1]
          const minPrice = Math.min(...futureCriteria.map(c => parseFloat(c.price)))
          // Les stocks négatifs (créneaux surréservés, « -4 ») sont ramenés à
          // zéro : les additionner tels quels retranchait des places réelles.
          const totalQty = futureCriteria.reduce(
            (sum, c) => {
              const n = parseInt(c.quantite, 10)
              return sum + (Number.isFinite(n) && n > 0 ? n : 0)
            },
            0
          )
          const levels = Array.from(
            new Set(
              futureCriteria
                .flatMap((c) => c.criterion)
                .map((l) => `${l.id}-${l.Name}-${l.Value}`)
            )
          ).join(', ')

          segmentedOffer.specificCriterion = {
            date: `${firstDate} ${lastDate}`,
            price: minPrice.toFixed(2),
            quantity: totalQty,
            level: levels,
          }
          segmentedOffer.ID_Criteria = `${offer.offerId} ${firstDate} - ${lastDate}`
          // Un stage/séjour est complet ssi TOUS ses créneaux futurs ont quantite <= 0
          // (le back peut renvoyer du négatif en cas d'overbooking — on le traite comme 0)
          const stageAllComplete = futureCriteria.every(c => (parseInt(c.quantite, 10) || 0) <= 0)
          segmentedOffer.CurrentQuantity = stageAllComplete ? '0' : '1'
          segmentedOffers.push(segmentedOffer)
        } else {
          // Cas autres types : grouper par date et garder tous les critères ensemble
          const groupedByDate = {};

          futureCriteria.forEach((c) => {
            const dateKey = c.date.split(' ')[0]; // Utiliser juste la date sans l'heure
            if (!groupedByDate[dateKey]) {
              groupedByDate[dateKey] = [];
            }
            groupedByDate[dateKey].push(c);
          });

          // Créer une entrée pour chaque date avec tous ses critères
          Object.entries(groupedByDate)
            .sort((a, b) => {
              const pa = new Date(a[0].split('/').reverse().join('-'));
              const pb = new Date(b[0].split('/').reverse().join('-'));
              return pa - pb;
            })
            .forEach(([dateKey, criteriaForDate]) => {
              // Calculer le prix min et max pour cette date
              const prices = criteriaForDate.map(c => parseFloat(c.price));
              const minPrice = Math.min(...prices);
              const maxPrice = Math.max(...prices);
              const priceDisplay = minPrice === maxPrice ? minPrice.toFixed(2) : `${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;

              // Collecter tous les critères groupés par créneau
              const allCriteria = criteriaForDate.map(c => ({
                criteria: c.criterion,
                price: c.price,
                quantity: c.quantite
              }));

              // Une date est complète ssi TOUS les créneaux ont quantite <= 0
              // (le back peut renvoyer du négatif en cas d'overbooking — on le traite comme 0)
              const allComplete = criteriaForDate.every(c => (parseInt(c.quantite, 10) || 0) <= 0);
              const currentQty = allComplete ? '0' : (criteriaForDate.find(c => (parseInt(c.quantite, 10) || 0) > 0)?.quantite || '0');

              segmentedOffers.push({
                ...JSON.parse(JSON.stringify(offer)),
                CurrentQuantity: currentQty,
                specificCriterion: {
                  date: criteriaForDate[0].date,
                  price: priceDisplay,
                  quantity: criteriaForDate[0].quantite,
                  level: allCriteria,
                  allCriteria: criteriaForDate // Garder tous les critères originaux
                },
                ID_Criteria: `${offer.offerId} ${dateKey}`,
              });
            });
        }
      })

      segmentedOffers.sort((a, b) => {
        const pa = new Date(
          a.specificCriterion.date.split(' ')[0].split('/').reverse().join('-')
        )
        const pb = new Date(
          b.specificCriterion.date.split(' ')[0].split('/').reverse().join('-')
        )
        return pa - pb
      })

      return segmentedOffers
    };


    onMounted(async () => {
      await fetchOffers();
      loading.value = true;
      error.value = false;

      try {
        const details = offers.value.offers.flatMap(offer =>
          offer.offerDetails.map(detail => ({
            id: detail.id,
            quantity: detail.quantity,            
          }))
        );

        const offerDetailsRequests = details.map(d =>
          axios.get(
            `https://www.ouicsport.fr/api/search/offer-details-json/${d.id}`
          )
        );

        const offerDetailsResponses = await Promise.all(offerDetailsRequests);

        const offerDetailsData = offerDetailsResponses.map((response, i) => {
          const detailData = JSON.parse(response.data["0"]);
          return {
            ...detailData,
            CurrentQuantity: details[i].quantity, 
          };
        });

        offerDetails.value = formatOfferDates(offerDetailsData);
        offerDetails.value = segmentOffersByCriteria(offerDetails.value);
        offerDetailsBackup.value = offerDetails.value;

        const urlParams = new URLSearchParams(window.location.search);
        const sport = urlParams.get("sport");
        if (sport) updateFilter(sport);

      } catch (err) {
        error.value = true;
        console.error(
          "Erreur lors de la récupération des détails de l'offre:",
          err
        );
      } finally {
        loading.value = false;
      }
    });



    

    const groupedOfferDetails = computed(() => {
      const grouped = {};

      // Première passe : regrouper les offres par titleOffers (ou type + ville si pas de titre)
      offerDetails.value.forEach(offer => {
        const key = offer.offerDetails.titleOffers || `${offer.offerDetails.type} à ${offer.offerDetails.finalCity}`;

        if (!grouped[key]) {
          grouped[key] = {
            ...offer,
            variants: [offer]
          };
        } else {
          grouped[key].variants.push(offer);
        }
      });

      // Si la carte principale tombe sur un variant complet alors qu'un autre
      // variant du groupe a des places, on promeut le premier variant dispo
      // en carte principale. Sinon la carte s'affiche "Complet" à tort.
      Object.values(grouped).forEach(group => {
        if (group.CurrentQuantity === '0') {
          const available = group.variants.find(v => v.CurrentQuantity !== '0');
          if (available) {
            const variants = group.variants;
            Object.assign(group, available);
            group.variants = variants;
          }
        }
      });

      // Deuxième passe : calculer les prix pour chaque groupe
      Object.values(grouped).forEach(group => {
        if (group.variants.length > 1) {
          const allPrices = group.variants.map(v => parseFloat(v.specificCriterion.price));
          const minPrice = Math.min(...allPrices);
          const maxPrice = Math.max(...allPrices);

          // Stocker le prix du groupe pour l'affichage de la carte principale
          // Ne pas modifier le prix des variants individuels
          if (minPrice !== maxPrice) {
            group.groupPrice = `${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;
          } else {
            group.groupPrice = minPrice.toFixed(2);
          }
        } else {
          group.groupPrice = group.specificCriterion.price;
        }
      });

      return Object.values(grouped);
    });

    const sortedOfferDetails = computed(() => {
      return groupedOfferDetails.value.slice().sort((a, b) => {
        const dateA = new Date(a.specificCriterion.date.split(' ')[0].split('/').reverse().join('-'));
        const dateB = new Date(b.specificCriterion.date.split(' ')[0].split('/').reverse().join('-'));
        return dateA - dateB;
      });
    });

    const showDatesModal = (offer) => {
      selectedOffer.value = offer;
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      selectedOffer.value = null;
    };

    const selectDate = (variant) => {
      // Rediriger vers la page de détails de cette variante spécifique
      router.push({ name: 'OfferDetailsPage', params: { id: variant.ID_Criteria } });
      closeModal();
    };

    /* ==================================================================
       Écran CATALOGUE de l'application — données propres au natif.
       Le site continue d'utiliser `sortedOfferDetails` tel quel ; rien de ce
       qui suit ne le concerne.
       ================================================================== */

    const acRecherche = ref('');
    const acType = ref('tous');

    const decodeEntites = (txt) => {
      if (!txt) return '';
      const el = document.createElement('textarea');
      el.innerHTML = txt;
      return el.value;
    };

    const TYPES_SEJOUR = ['Stage', 'Séjour', 'Stage à la carte', 'Stage/Séjour'];

    /**
     * Niveau requis. Selon le type d'offre, la segmentation le range soit dans
     * un tableau `criterion`, soit aplati dans une chaîne `level` au format
     * « id-Nom-Valeur », plusieurs entrées séparées par des virgules.
     * On lit les deux, et on reconstitue les valeurs contenant des tirets.
     */
    const litNiveaux = (sc) => {
      if (Array.isArray(sc?.criterion)) {
        const v = sc.criterion.find((c) => c.Name === 'Niveaux');
        if (v && v.Value) return v.Value;
      }
      if (typeof sc?.level === 'string' && sc.level.trim()) {
        const vals = sc.level.split(',')
          .map((x) => x.trim().split('-'))
          .filter((parts) => parts[1] === 'Niveaux')
          .map((parts) => parts.slice(2).join('-').trim())
          .filter(Boolean);
        if (vals.length) return [...new Set(vals)].join(' · ');
      }
      return null;
    };

    /** Une ligne par date proposée, avec tout ce qu'affiche la carte. */
    const acLignes = computed(() => (sortedOfferDetails.value || []).map((e) => {
      const od = e.offerDetails || {};
      const sc = e.specificCriterion || {};
      const niveau = litNiveaux(sc);
      const nbDates = Array.isArray(e.variants) ? e.variants.length : 0;
      return {
        cle: `${e.offerId}-${sc.date}`,
        id: e.ID_Criteria,
        entree: e,
        sport: od.sport || '',
        type: od.type || '',
        titre: decodeEntites(od.titleOffers) || `${od.type} à ${od.finalCity}`,
        ville: od.finalCity || '',
        club: od.clubName || '',
        prix: sc.price ? Math.round(parseFloat(sc.price)) : (e.price || null),
        niveau,
        image: apiImg((e.images || [])[0] || ''),
        date: sc.date || '',
        nbDates,
        complet: String(e.CurrentQuantity) === '0',
      };
    }));

    /** Filtres cumulés : sport (déjà géré par updateFilter), type, recherche. */
    const acVisibles = computed(() => {
      const q = acRecherche.value.trim().toLowerCase();
      return acLignes.value.filter((l) => {
        if (acType.value === 'sessions' && TYPES_SEJOUR.includes(l.type)) return false;
        if (acType.value === 'sejours' && !TYPES_SEJOUR.includes(l.type)) return false;
        if (!q) return true;
        return `${l.titre} ${l.ville} ${l.club} ${l.sport}`.toLowerCase().includes(q);
      });
    });

    /* La recherche n'apparaît qu'à partir du moment où elle sert réellement.
       Sur un catalogue de quatre sorties, un champ de recherche est un décor :
       on le montre quand la liste devient assez longue pour qu'on s'y perde. */
    const acRechercheUtile = computed(() => acLignes.value.length >= 8);

    return {
      offers,
      sportNames,
      acLignes,
      acVisibles,
      acRecherche,
      acType,
      acRechercheUtile,
      filteredOffers,
      selectedFilter,
      updateFilter,
      offerDetails,
      sortedOfferDetails,
      loading,
      error,
      offerDetailsBackup,
      isGrayedOut,
      showModal,
      selectedOffer,
      showDatesModal,
      closeModal,
      selectDate,
      apiImg,
      sportBanner,
      isNative
    };
  }
}
</script>

<style>
/* =========================================================================
   CATALOGUE — feuille de l'application, écrite de zéro
   =========================================================================
   Balisage natif distinct de celui du site : rien n'est hérité ni combattu.
   Sélecteurs préfixés `body.is-native` pour que le contrôle automatique
   vérifie que le site reste intact.

   Écran de parcours : la densité prime. D'où une liste en lignes plutôt
   qu'une grille — on voit davantage d'activités par écran, et la vignette
   latérale de 120px maintient les photos (414x219 au maximum côté serveur)
   dans leur zone de netteté.
   ========================================================================= */

body.is-native .ac-cat,
body.is-native .ac-cat *,
body.is-native .ac-cat *::before,
body.is-native .ac-cat *::after {
  box-sizing: border-box;
}

/* Remise à zéro au niveau des éléments : la feuille du site cible aussi des
   éléments nus (`header` peint en blanc, `h1`, `h2`, `p`, `button`, `input`),
   et les rattraperait malgré un balisage neuf. */
body.is-native .ac-cat header,
body.is-native .ac-cat h1,
body.is-native .ac-cat h2,
body.is-native .ac-cat p,
body.is-native .ac-cat span,
body.is-native .ac-cat div,
body.is-native .ac-cat article,
body.is-native .ac-cat button,
body.is-native .ac-cat input,
body.is-native .ac-cat img {
  all: unset;
  display: revert;
  /* `all: unset` retire le `overflow: clip` que le navigateur applique
     aux éléments remplacés : sans lui, le débordement d'`object-fit:
     cover` se peint HORS du cadre de l'image. */
  overflow: clip;
  box-sizing: border-box;
}

body.is-native .ac-cat {
  --ac-bg: #F5F0E6;
  --ac-surface: #EFE8DA;
  --ac-raised: #E5DDD0;
  --ac-dark: #2B231F;
  --ac-ink: #28201C;
  --ac-ink-2: #6B5E54;
  --ac-ink-3: #8C7E73;
  --ac-on-dark: #FAF6EE;
  --ac-red: #DD4124;
  --ac-red-soft: rgba(221, 65, 36, 0.12);
  --ac-hair: rgba(107, 94, 84, 0.14);

  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--ac-bg);
  font-family: 'Dosis', system-ui, sans-serif;
  color: var(--ac-ink);
  -webkit-user-select: none;
  user-select: none;
}

/* ---------- Bandeau de contrôles ---------- */

body.is-native .ac-cat .ac-top {
  padding: calc(env(safe-area-inset-top, 0px) + 12px) 16px 12px;
  background: var(--ac-bg);
  border-bottom: 1px solid var(--ac-hair);
}

body.is-native .ac-cat .ac-top__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

body.is-native .ac-cat .ac-top__title {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 28px;
  line-height: 1;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ac-ink);
}

body.is-native .ac-cat .ac-top__count {
  flex: 0 0 auto;
  padding: 5px 11px;
  font-size: 14px;
  font-weight: 700;
  color: var(--ac-ink-2);
  background: var(--ac-surface);
  border: 1px solid var(--ac-hair);
  border-radius: 12px;
}

/* ---------- Recherche ---------- */

body.is-native .ac-cat .ac-search {
  position: relative;
  margin-bottom: 10px;
}

body.is-native .ac-cat .ac-search__icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ac-ink-3);
  pointer-events: none;
}

body.is-native .ac-cat .ac-search__field {
  width: 100%;
  height: 44px;
  padding: 0 40px;
  /* 16px minimum : en dessous, iOS zoome tout seul à la mise au point. */
  font-family: 'Dosis', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--ac-ink);
  background: var(--ac-surface);
  border: 1px solid var(--ac-hair);
  border-radius: 14px;
  transition: border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ac-cat .ac-search__field::placeholder {
  color: var(--ac-ink-3);
  font-weight: 500;
}

body.is-native .ac-cat .ac-search__field:focus {
  border-color: var(--ac-red);
}

body.is-native .ac-cat .ac-search__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: var(--ac-ink-3);
  cursor: pointer;
}

/* ---------- Filtres ---------- */

body.is-native .ac-cat .ac-sports,
body.is-native .ac-cat .ac-formats {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  scroll-padding-left: 16px;
}

body.is-native .ac-cat .ac-sports::-webkit-scrollbar,
body.is-native .ac-cat .ac-formats::-webkit-scrollbar { display: none; }

body.is-native .ac-cat .ac-formats { margin-top: 8px; }

body.is-native .ac-cat .ac-sport {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ac-ink-2);
  background: var(--ac-surface);
  border: 1px solid var(--ac-hair);
  border-radius: 19px;
  cursor: pointer;
  transition: background 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ac-cat .ac-sport.is-on {
  color: var(--ac-on-dark);
  background: var(--ac-red);
  border-color: var(--ac-red);
}

body.is-native .ac-cat .ac-sport:active,
body.is-native .ac-cat .ac-format:active { transform: scale(0.95); }

body.is-native .ac-cat .ac-format {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ac-ink-3);
  background: transparent;
  border: 1px solid rgba(107, 94, 84, 0.2);
  border-radius: 16px;
  cursor: pointer;
  transition: background 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ac-cat .ac-format.is-on {
  color: var(--ac-on-dark);
  background: var(--ac-dark);
  border-color: var(--ac-dark);
}

/* ---------- Liste ---------- */

body.is-native .ac-cat .ac-body {
  flex: 1;
  padding: 12px 16px var(--oc-sp-6);
}

body.is-native .ac-cat .ac-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

body.is-native .ac-cat .ac-row {
  display: block;
  overflow: hidden;
  background: var(--ac-surface);
  border: 1px solid var(--ac-hair);
  border-radius: 18px;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ac-cat .ac-row:active { transform: scale(0.98); }
body.is-native .ac-cat .ac-row.is-full { opacity: 0.85; }

body.is-native .ac-cat .ac-row__hit {
  display: flex;
  align-items: stretch;
  min-height: 124px;
  cursor: pointer;
}

/* Vignette bridée à 120px : au-delà, la source 414x219 ne suffirait plus
   pour un écran en densité double. */
body.is-native .ac-cat .ac-row__thumb {
  position: relative;
  flex: 0 0 120px;
  width: 120px;
  overflow: hidden;
  background: var(--ac-raised);
}

body.is-native .ac-cat .ac-row__thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

body.is-native .ac-cat .ac-row__type,
body.is-native .ac-cat .ac-row__done {
  position: absolute;
  left: 6px;
  display: block;
  max-width: 108px;
  padding: 3px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: var(--ac-on-dark);
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ac-cat .ac-row__type { top: 6px; background: rgba(43, 35, 31, 0.85); }
body.is-native .ac-cat .ac-row__done { bottom: 6px; background: var(--ac-red); font-weight: 800; }

body.is-native .ac-cat .ac-row__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
}

body.is-native .ac-cat .ac-row__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}

body.is-native .ac-cat .ac-row__sport {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ac-red);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ac-cat .ac-row__dates {
  flex: 0 0 auto;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 800;
  color: var(--ac-red);
  background: var(--ac-red-soft);
  border: 1px solid rgba(221, 65, 36, 0.2);
  border-radius: 10px;
}

/* Deux lignes, pas une : sur une seule, « Canoë et barbecue à Pommeuse (77) »
   se réduisait à « Canoë et barbecue à Pomm… » — l'essentiel disparaissait. */
body.is-native .ac-cat .ac-row__title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 6px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ac-ink);
  overflow: hidden;
}

body.is-native .ac-cat .ac-row__line {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 2px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ac-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ac-cat .ac-row__line svg { flex: 0 0 auto; }

body.is-native .ac-cat .ac-row__club {
  font-size: 12px;
  font-weight: 500;
  color: var(--ac-ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ac-cat .ac-row__foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 6px;
  border-top: 1px dashed rgba(107, 94, 84, 0.18);
}

body.is-native .ac-cat .ac-row__level {
  min-width: 0;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ac-ink-2);
  background: var(--ac-raised);
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .ac-cat .ac-row__price {
  flex: 0 0 auto;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  color: var(--ac-ink);
  font-variant-numeric: tabular-nums;
}

body.is-native .ac-cat .ac-row__price span {
  font-size: 13px;
  font-weight: 600;
}

/* ---------- Chargement et absence de résultat ---------- */

body.is-native .ac-cat .ac-row--ghost {
  display: block;
  height: 124px;
  border: 0;
  background: linear-gradient(90deg, var(--ac-surface) 25%, var(--ac-raised) 37%, var(--ac-surface) 63%);
  background-size: 400% 100%;
  animation: ac-shimmer 1.4s ease infinite;
}

@keyframes ac-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

body.is-native .ac-cat .ac-void {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 20px;
  text-align: center;
}

body.is-native .ac-cat .ac-void__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-bottom: 16px;
  color: var(--ac-red);
  background: var(--ac-raised);
  border: 1px solid var(--ac-hair);
  border-radius: 36px;
}

body.is-native .ac-cat .ac-void__title {
  margin-bottom: 8px;
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 24px;
  text-transform: uppercase;
  color: var(--ac-ink);
}

body.is-native .ac-cat .ac-void__text {
  max-width: 30ch;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--ac-ink-2);
}

body.is-native .ac-cat .ac-void__reset {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ac-on-dark);
  background: var(--ac-red);
  border-radius: 22px;
  cursor: pointer;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ac-cat .ac-void__reset:active { transform: scale(0.96); }

@media (prefers-reduced-motion: reduce) {
  body.is-native .ac-cat .ac-row--ghost { animation: none; }
}
</style>
